import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { formatMetricValue } from '../utils/riskCalculator';
import { useTranslation } from '../hooks/useTranslation';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * MetricCard component displays individual metric with status and trend
 */
export default function MetricCard({ 
  title, 
  value, 
  status = 'green', 
  format = 'percentage',
  description,
  history = [],
  threshold
}) {
  const { t } = useTranslation();
  // Status color mapping
  const statusColors = {
    green: {
      border: 'border-success',
      bg: 'bg-success/10',
      text: 'text-success',
      badge: 'bg-success'
    },
    yellow: {
      border: 'border-warning',
      bg: 'bg-warning/10',
      text: 'text-warning',
      badge: 'bg-warning'
    },
    red: {
      border: 'border-danger',
      bg: 'bg-danger/10',
      text: 'text-danger',
      badge: 'bg-danger'
    },
    gray: {
      border: 'border-gray-300',
      bg: 'bg-gray-100',
      text: 'text-gray-500',
      badge: 'bg-gray-400'
    }
  };

  const colors = statusColors[status] || statusColors.gray;

  // Prepare chart data
  const chartData = {
    labels: history.slice(-20).map((_, i) => ''), // Empty labels for sparkline
    datasets: [
      {
        data: history.slice(-20).map(point => point.value),
        borderColor: status === 'red' ? '#EF4444' : status === 'yellow' ? '#F59E0B' : '#10B981',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 60);
          const color = status === 'red' ? '239, 68, 68' : status === 'yellow' ? '245, 158, 11' : '16, 185, 129';
          gradient.addColorStop(0, `rgba(${color}, 0.2)`);
          gradient.addColorStop(1, `rgba(${color}, 0)`);
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    },
    scales: {
      x: { display: false },
      y: { display: false }
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border-l-4 ${colors.border} transition-all hover:shadow-lg`}>
      {/* Header */}
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          {title}
        </h3>
        <span className={`${colors.badge} text-white text-xs font-semibold px-2 py-1 rounded-full`}>
          {t(`status.${status}`)}
        </span>
      </div>

      {/* Value */}
      <div className={`text-3xl font-bold ${colors.text} mb-2`}>
        {value !== null && value !== undefined ? formatMetricValue(value, format) : 'N/A'}
      </div>

      {/* Description */}
      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          {description}
        </p>
      )}

      {/* Threshold info */}
      {threshold && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
          {t('status.threshold')}: {threshold}
        </p>
      )}

      {/* Mini chart */}
      {history.length > 1 && (
        <div className="h-16 mt-3">
          <Line data={chartData} options={chartOptions} />
        </div>
      )}

      {/* Trend indicator */}
      {history.length >= 2 && (
        <div className="flex items-center mt-2 text-xs">
          {history[history.length - 1].value > history[history.length - 2].value ? (
            <span className="text-red-500 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v10.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              {t('status.rising')}
            </span>
          ) : history[history.length - 1].value < history[history.length - 2].value ? (
            <span className="text-green-500 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 17a1 1 0 01-1-1V5.414L6.707 7.707a1 1 0 11-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V16a1 1 0 01-1 1z" clipRule="evenodd" />
              </svg>
              {t('status.falling')}
            </span>
          ) : (
            <span className="text-gray-500">{t('status.stable')}</span>
          )}
        </div>
      )}
    </div>
  );
}

